import React, { useCallback, useEffect, useState } from 'react';

import { getIpdDashboard } from '../../services/api/ipdDashboardApi';
import { usePermission } from '../../context/PermissionContext';
import { REPORT_PERMISSIONS, NO_PATIENT_PANE_PLACEHOLDER_COPY } from '../../constants/admission';
import { formatPaise } from '../../utils/money';
import DepositBreakdownModal from './DepositBreakdownModal';
import BackdatedCompletionsLine from './BackdatedCompletionsLine';
import { selectBackdatedCompletions } from './backdatedCompletions';

/**
 * AdmissionDashboardPanel (S2 · FE-A) — the default "Admission Dashboard"
 * shown in the right workspace pane when no admission is pinned (`view ===
 * 'dashboard'`, see AdmissionDeskPage). Binds to the ONE frozen aggregate
 * read `GET /reports/ipd-dashboard` (C-#2) — every tile renders a server
 * field verbatim; nothing here re-derives money/bed/low-deposit rules
 * (those live server-side, single-sourced with the KPI strip / deposits
 * report / bed board).
 *
 * Fetch-on-entry only (mount) — no polling, no WS refetch. One request per
 * pane-entry; the parent remounts this panel (fresh mount) whenever the
 * user returns to the dashboard view, so a fresh fetch on each entry is the
 * single-flight-safe way to keep it current without an interval.
 *
 * admission-access S1-AC3 — a member without VIEW_IPD_DASHBOARD (doctor,
 * nurse) sees a plain placeholder instead, and the aggregate is never
 * requested (the server would refuse it, and an error box is not an answer).
 */
export default function AdmissionDashboardPanel() {
  const canViewDashboard = usePermission(REPORT_PERMISSIONS.VIEW_IPD_DASHBOARD);
  const [state, setState] = useState('loading'); // 'loading' | 'error' | 'data'
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // S2 drill-downs (#5/#6) — which breakdown modal is open, if any. The
  // modal component only MOUNTS on a tile click (never on dashboard load,
  // so it can't regress the #1 perf fix); it fetches its own rows on mount.
  const [modalVariant, setModalVariant] = useState(null); // null | 'held' | 'today'

  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    const res = await getIpdDashboard();
    if (res.success) {
      setData(res.data);
      setState('data');
    } else {
      setError(res.error || "Couldn't load the dashboard.");
      setState('error');
    }
  }, []);

  useEffect(() => {
    if (canViewDashboard) load();
  }, [canViewDashboard, load]);

  if (!canViewDashboard) {
    return (
      <div className="p-6" data-testid="dashboard-placeholder">
        <p className="text-[12.5px] text-slate-500 dark:text-slate-400">{NO_PATIENT_PANE_PLACEHOLDER_COPY}</p>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admission-dashboard-panel">
      <div className="mb-4">
        <h2 className="text-[16px] font-bold text-slate-900 dark:text-white">Admission Dashboard</h2>
        {/* COPY: Business-ratified */}
        <p className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-0.5">
          Select an admission on the left to view its bill and actions.
        </p>
      </div>

      {state === 'loading' && <DashboardSkeleton />}

      {state === 'error' && (
        <div
          className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-3 flex items-center justify-between gap-3"
          data-testid="dashboard-error"
        >
          {/* COPY: Business-ratified */}
          <span>{error || "Couldn't load the dashboard."}</span>
          <button
            type="button"
            onClick={load}
            className="px-3 py-1.5 rounded-lg text-[12px] font-semibold border border-red-400 dark:border-red-600 bg-white dark:bg-red-900/40 text-red-800 dark:text-red-100 hover:bg-red-100 dark:hover:bg-red-800 flex-none"
            data-testid="dashboard-retry"
          >
            Retry
          </button>
        </div>
      )}

      {state === 'data' && data && (
        <DashboardTiles data={data} onOpenModal={setModalVariant} />
      )}

      {modalVariant && (
        <DepositBreakdownModal
          variant={modalVariant}
          onClose={() => setModalVariant(null)}
        />
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3" data-testid="dashboard-skeleton">
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-4 min-h-[100px] animate-pulse">
          <div className="h-2.5 w-16 rounded bg-slate-200 dark:bg-slate-700 mb-3" />
          <div className="h-7 w-20 rounded bg-slate-200 dark:bg-slate-700 mb-2" />
          <div className="h-2.5 w-24 rounded bg-slate-200 dark:bg-slate-700" />
        </div>
      ))}
    </div>
  );
}

// `onClick` (+ `ariaLabel`) makes a tile a real `<button>` — keyboard
// activable (Enter/Space, native) with a visible hover/focus affordance
// that it opens something. Tiles without `onClick` render as a plain,
// non-interactive `<div>` (unchanged from before).
function Tile({ testId, label, value, sub, warn, onClick, ariaLabel }) {
  const interactive = typeof onClick === 'function';
  const baseCls = warn
    ? 'rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-4 py-4 min-h-[100px]'
    : 'rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-4 min-h-[100px]';
  const interactiveCls = interactive
    ? ' text-left w-full cursor-pointer hover:border-teal-400 dark:hover:border-teal-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500'
    : '';

  const content = (
    <>
      <div className="flex items-start justify-between gap-1">
        <div className={`text-[11px] font-bold uppercase tracking-wide ${warn ? 'text-amber-700 dark:text-amber-300' : 'text-slate-400 dark:text-slate-500'}`}>
          {label}
        </div>
        {/* Visible clickable affordance — decorative chevron, not copy. */}
        {interactive && (
          <span className="text-slate-300 dark:text-slate-600 text-[13px] leading-none" aria-hidden="true">
            &rsaquo;
          </span>
        )}
      </div>
      <div className={`text-[26px] font-extrabold leading-tight tabular-nums mt-1 ${warn ? 'text-amber-800 dark:text-amber-200' : 'text-slate-900 dark:text-white'}`}>
        {value}
      </div>
      <div className={`text-[12px] mt-1 leading-snug ${warn ? 'text-amber-700 dark:text-amber-300 font-semibold' : 'text-slate-500 dark:text-slate-400'}`}>
        {sub}
      </div>
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={baseCls + interactiveCls}
        data-testid={testId}
        aria-label={ariaLabel}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={baseCls} data-testid={testId}>
      {content}
    </div>
  );
}

// COPY: Business-ratified for all tiles below, including the two discharge tiles.
// `onOpenModal(variant)` (S2 drill-downs #5/#6) is NEW — see the
// "Deposit held" / "Deposit collected today" tiles below, marked pending
// Business ratification for their aria-labels.
function DashboardTiles({ data, onOpenModal }) {
  const beds = data.beds || { configured: false, occupied: 0, total: null, free: null };
  const lowDepositCount = Number(data.low_deposit_count) || 0;
  const admittedCount = Number(data.admitted_count) || 0;
  const lowWarn = lowDepositCount > 0;
  const lowSub = lowWarn
    ? "Some patients' bills have crossed their deposit."
    : admittedCount === 0
      ? 'No patients admitted yet.'
      : 'All patients have enough deposit for now.';

  const bedsLabel = beds.configured ? 'Beds occupied / free' : 'Beds occupied';
  const bedsValue = beds.configured ? `${beds.occupied} / ${beds.total}` : String(beds.occupied);
  const bedsSub = beds.configured
    ? `${beds.free} free of ${beds.total} total`
    : 'Set up wards and bed capacity in Settings to see beds free.';

  const dischargeInitiatedCount = Number(data.discharge_initiated_count) || 0;
  const dischargedTodayCount = Number(data.discharged_today_count) || 0;

  // "Deposit collected today" now shows Collected / Refunded / Net. Collected
  // and Refunded come verbatim from the server (never re-derived); Net is the
  // ONE derivation on this dashboard — a single subtraction, here, in one
  // place. `refunded_today_paise` defaults to 0 so a legacy response (field
  // absent) renders cleanly with Net == Collected and no refund line.
  const collectedTodayPaise = Number(data.collected_today_paise) || 0;
  const refundedTodayPaise = Number(data.refunded_today_paise) || 0;
  const netTodayPaise = collectedTodayPaise - refundedTodayPaise;

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3" data-testid="dashboard-tiles">
      <Tile
        testId="dashboard-tile-admitted"
        label="Patients admitted"
        value={admittedCount}
        sub="Currently admitted, across all wards"
      />
      <Tile
        testId="dashboard-tile-deposit-held"
        label="Deposit held"
        value={formatPaise(data.deposit_held_paise)}
        sub="Held across all admitted patients"
        onClick={() => onOpenModal('held')}
        ariaLabel="Deposit held. View patient-wise breakdown."
      />
      <Tile
        testId="dashboard-tile-collected-today"
        label="Deposit collected today"
        value={formatPaise(data.collected_today_paise)}
        sub={
          refundedTodayPaise > 0 ? (
            // On a day with refunds: show the amber Refunded line (magnitude
            // as −₹Y, matching the deposits report dialog's refund tone) and
            // the derived Net. The headline value stays Collected so it still
            // reconciles with the "today" breakdown modal total.
            <span className="block" data-testid="collected-today-breakdown">
              <span
                className="flex justify-between gap-2 tabular-nums font-semibold text-amber-700 dark:text-amber-400"
                data-testid="collected-today-refunded"
              >
                <span>Refunded</span>
                <span>{formatPaise(-refundedTodayPaise)}</span>
              </span>
              <span
                className="flex justify-between gap-2 tabular-nums font-semibold mt-0.5 text-slate-700 dark:text-slate-200"
                data-testid="collected-today-net"
              >
                <span>Net</span>
                <span>{formatPaise(netTodayPaise)}</span>
              </span>
            </span>
          ) : (
            'Cash, UPI and card taken today.'
          )
        }
        onClick={() => onOpenModal('today')}
        ariaLabel="Deposit collected today. View the breakup."
      />
      {/* Low-deposit tile is INFORMATIONAL day-1 — count only, NOT
          clickable (no drill-down target exists yet). */}
      <Tile
        testId="dashboard-tile-low-deposit"
        label="Low-deposit patients"
        value={lowDepositCount}
        sub={lowSub}
        warn={lowWarn}
      />
      <Tile
        testId="dashboard-tile-beds"
        label={bedsLabel}
        value={bedsValue}
        sub={bedsSub}
      />
      {/* Discharge in progress / Discharged today — informational (no
          drill-down target), matches the low-deposit tile pattern.
          The "Discharge in progress" tile counts BOTH DISCHARGE_INITIATED +
          BILLING_CLEARANCE, matching the rail chip. COPY: Business-ratified. */}
      <Tile
        testId="dashboard-tile-discharge-initiated"
        label="Discharge in progress"
        value={dischargeInitiatedCount}
        sub="Patients whose discharge has started"
      />
      <Tile
        testId="dashboard-tile-discharged-today"
        label="Discharged today"
        value={dischargedTodayCount}
        sub={(
          <>
            <span className="block">Completed discharges today</span>
            <BackdatedCompletionsLine items={selectBackdatedCompletions(data)} testId="dashboard-backdated" />
          </>
        )}
      />
    </div>
  );
}
